import { baseApi } from "./baseApi";

/**
 * Local types for Monetize Knowledge leads.
 * These match the fields expected by the MonetizeKnowledgeModel.js backend.
 */
export type MonetizeKnowledgeLead = {
  _id?: string;
  firstName: string;
  email: string;
  phone: string; // Changed from phoneNo to match the model
  eventCity?: string;
  eventDate?: string; // Stored as ISO string
  venue?: string;
  source?: string;
  status?: "registered" | "confirmed" | "checked_in" | "no_show" | "cancelled";
  metrics?: {
    reminderEmailSent?: boolean;
    reminderSmsSent?: boolean;
    postEventFollowUpSent?: boolean;
    ticketDownloaded?: boolean;
  };
  consent?: {
    marketing?: boolean;
    termsAccepted?: boolean;
    consentGivenAt?: string | null;
  };
  meta?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateMonetizeKnowledgeRequest = Partial<
  Pick<
    MonetizeKnowledgeLead,
    | "firstName"
    | "email"
    | "phone"
    | "eventCity"
    | "eventDate"
    | "venue"
    | "source"
    | "meta"
  >
>;

export type UpdateMonetizeKnowledgeRequest = Partial<
  Pick<
    MonetizeKnowledgeLead,
    | "firstName"
    | "email"
    | "phone"
    | "eventCity"
    | "eventDate"
    | "status"
    | "metrics"
    | "source"
  >
>;

/**
 * monetizeKnowledgeApi - RTK Query endpoints for the Monetize Knowledge lead CRUD.
 * Routes map to /api/monetize-knowledge
 */
export const monetizeKnowledgeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // 1. Create / Register a lead for the event
    createMonetizeKnowledgeLead: builder.mutation<
      MonetizeKnowledgeLead,
      CreateMonetizeKnowledgeRequest
    >({
      query: (data) => ({
        url: "/monetize-knowledge",
        method: "POST",
        body: data,
      }),
      // Invalidates the list cache to show the new registration immediately
      invalidatesTags: [{ type: "MonetizeKnowledgeLead", id: "LIST" }],
    }),

    // 2. Get all leads
    getMonetizeKnowledgeLeads: builder.query<MonetizeKnowledgeLead[], void>({
      query: () => ({
        url: "/monetize-knowledge",
        method: "GET",
      }),
      // Provides tags for caching and invalidation
      providesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({
                type: "MonetizeKnowledgeLead" as const,
                id: r._id,
              })),
              { type: "MonetizeKnowledgeLead" as const, id: "LIST" },
            ]
          : [{ type: "MonetizeKnowledgeLead" as const, id: "LIST" }],
    }),

    // 3. Get single lead by id
    getMonetizeKnowledgeLead: builder.query<MonetizeKnowledgeLead, string>({
      query: (id) => ({
        url: `/monetize-knowledge/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [
        { type: "MonetizeKnowledgeLead", id },
      ],
    }),

    // 4. Update a lead (e.g., change status to 'checked_in')
    updateMonetizeKnowledgeLead: builder.mutation<
      MonetizeKnowledgeLead,
      { id: string; data: UpdateMonetizeKnowledgeRequest }
    >({
      query: ({ id, data }) => ({
        url: `/monetize-knowledge/${id}`,
        method: "PUT",
        body: data,
      }),
      // Invalidates both the specific item and the list
      invalidatesTags: (result, error, { id }) => [
        { type: "MonetizeKnowledgeLead", id },
        { type: "MonetizeKnowledgeLead", id: "LIST" },
      ],
    }),

    // 5. Delete a lead
    deleteMonetizeKnowledgeLead: builder.mutation<
      MonetizeKnowledgeLead,
      string
    >({
      query: (id) => ({
        url: `/monetize-knowledge/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "MonetizeKnowledgeLead", id },
        { type: "MonetizeKnowledgeLead", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

/**
 * Export hooks for usage in React components
 *
 */
export const {
  useCreateMonetizeKnowledgeLeadMutation,
  useGetMonetizeKnowledgeLeadsQuery,
  useGetMonetizeKnowledgeLeadQuery,
  useUpdateMonetizeKnowledgeLeadMutation,
  useDeleteMonetizeKnowledgeLeadMutation,
} = monetizeKnowledgeApi;
